import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Bot, X, Paperclip, History, MessageSquarePlus, MessageSquare, CircleDotDashed, CheckCircle2, CircleX } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type ChatMessage = {
  id: string;
  role: 'user' | 'bot' | 'system';
  text: string;
  pending?: boolean;
  attachments?: { name: string }[];
};

// A persisted conversation, scoped to the current visitor. `id` is the same
// session id the widget hands to the WS so AgentCore memory continuity is
// preserved when a past session is resumed. Stored client-side only.
type StoredSession = {
  id: string;
  title: string;
  messages: ChatMessage[];
  msgCount: number;
  updatedAt: number;
};

// A file the visitor is attaching to the next message. It uploads straight to
// S3 via a presigned URL minted by the Channel runtime (widget_presign), then
// its s3_key rides in widget_message → the deployed agent fetches + reads it.
type PendingAttachment = {
  id: string;
  name: string;
  size: number;
  mime: string;
  status: 'uploading' | 'ready' | 'error';
  s3Key?: string;
  bucket?: string;
};

type UploadsConfig = {
  enabled: boolean;
  maxBytes: number;
  maxFiles: number;
  allowedExt: string[];
};

// One step in the live agent-activity log for the current turn.
type ActivityStep = { label: string; state: 'active' | 'done' | 'error' };

// Tool name → friendly "what's happening" phrasing (mirrors basetemplate's
// lib/tool-labels). Falls back to humanizing the raw name so we never show a
// raw snake_case identifier like `deck_processor` or `web_search`.
const ACTIVITY_TOOL_LABELS: Record<string, string> = {
  web_search: 'Searching the web',
  search: 'Searching',
  tavily_search: 'Searching the web',
  brave_search: 'Searching the web',
  playwright_scraper: 'Reading web pages',
  knowledge_base_query: 'Searching the knowledge base',
  search_documents: 'Searching documents',
  search_dataset: 'Searching the dataset',
  rag: 'Searching the knowledge base',
  generate_report: 'Generating the report',
  generate_image: 'Generating an image',
  http_request: 'Calling an external service',
  ask_human: 'Waiting for your answer',
};

// Strip AgentCore Gateway target prefix (`target___tool`) + mcp/integration
// prefixes, then humanize. Agents drop a trailing agent/specialist/service/bot
// suffix; meaningful role words (analyst, validator, processor…) are kept.
function prettyActivity(kind: 'agent' | 'tool', raw: string): string {
  let bare = raw.includes('___') ? raw.slice(raw.indexOf('___') + 3) : raw;
  bare = bare.replace(/^(mcp|integration|connector|rag_mcp_server)[:_-]/i, '');
  if (ACTIVITY_TOOL_LABELS[bare]) return ACTIVITY_TOOL_LABELS[bare];
  let words = bare;
  if (kind === 'agent') words = words.replace(/[-_]?(agent|specialist|service|bot)$/i, '');
  words = words.replace(/[-_]+/g, ' ').trim() || bare;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Human-in-the-loop: the agent pauses mid-run (ask_human) and asks the visitor
// one or more questions; we show a form and the SAME run resumes with the answer.
type HitlQuestion = { question: string; type?: string; options?: string[]; allow_multi?: boolean };
type HitlRequest = {
  interrupt_id: string;
  name?: string;
  kind?: string; // "ask" | "approve" | "delegate" | "tool" | "output_review"
  question?: string;
  options?: string[];
  questions?: HitlQuestion[];
  agent?: string;
  tool?: string;
  tool_input?: unknown;
  draft?: string;
};
function humanizeName(s?: string): string {
  if (!s) return '';
  return s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}
const HITL_TOOL_PHRASES: Record<string, string> = {
  web_search: 'search the web', rag: 'search the knowledge base',
  knowledge_base_query: 'search the knowledge base', search_documents: 'search the documents',
  generate_report: 'create a report', generate_image: 'generate an image',
  send_email: 'send an email', http_request: 'call an external service',
};
function toolPhrase(tool?: string): string {
  if (!tool) return 'take an action';
  return HITL_TOOL_PHRASES[tool] || `use ${humanizeName(tool).toLowerCase()}`;
}
// Two shapes of pause: an "ask" (the agent asks YOU a question → free-form/MCQ
// answer) vs an approval gate (approve / edit / reject a delegate, tool, or output).
function isAskHitl(r: HitlRequest): boolean {
  const kind = r.kind || (r.name === 'ask_human' ? 'ask' : 'approve');
  return kind === 'ask' || r.name === 'ask_human' || (Array.isArray(r.questions) && r.questions.length > 0);
}
function hitlHeadline(r: HitlRequest): string {
  const agent = humanizeName(r.agent) || 'The assistant';
  if (r.kind === 'output_review') return `Review ${agent}'s work before it continues`;
  if (r.kind === 'delegate') return `The assistant wants to bring in ${agent} — approve?`;
  return `${agent} wants to ${toolPhrase(r.tool)} — approve?`;
}
// Normalize either ask shape (multi `questions` or single `question`+`options`) to a list.
function hitlQuestions(r: HitlRequest): HitlQuestion[] {
  if (r.questions && r.questions.length) return r.questions;
  return [{
    question: r.question || 'The assistant needs your input.',
    options: r.options || [],
    allow_multi: false,
    type: r.options && r.options.length ? 'choice' : 'text',
  }];
}


export type FloatingAiAssistantProps = {
  /** Bot ID issued by Qubitz when you create the widget install. */
  botId?: string;
  /** WebSocket URL of the channels-api-proxy (or local Channel runtime in dev). */
  wsUrl?: string;
};

const DEFAULT_WS_URL =
  typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? 'wss://channels.qubitz.ai/ws'
    : 'ws://127.0.0.1:8090/ws';

const VISITOR_KEY = 'qubitz.widget.visitorId';
// Past conversations live entirely in localStorage, keyed per (bot, visitor)
// so the same browser shows the right history regardless of which install
// is embedded on the page.
const SESSIONS_KEY_PREFIX = 'qubitz.widget.sessions';
const MAX_STORED_SESSIONS = 40;

const sessionsStorageKey = (botId: string, visitorId: string) =>
  `${SESSIONS_KEY_PREFIX}.${botId}.${visitorId || 'anon'}`;

const loadSessions = (botId: string, visitorId: string): StoredSession[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(sessionsStorageKey(botId, visitorId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (s) => s && typeof s.id === 'string' && Array.isArray(s.messages),
    ) as StoredSession[];
  } catch {
    return [];
  }
};

const saveSessions = (botId: string, visitorId: string, sessions: StoredSession[]) => {
  if (typeof window === 'undefined') return;
  try {
    const trimmed = [...sessions]
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, MAX_STORED_SESSIONS);
    window.localStorage.setItem(sessionsStorageKey(botId, visitorId), JSON.stringify(trimmed));
  } catch {
    /* quota / serialization failure — history is best-effort, ignore */
  }
};

// Title = first user message, trimmed to a single line and capped.
const deriveTitle = (messages: ChatMessage[]): string => {
  const firstUser = messages.find((m) => m.role === 'user' && m.text.trim());
  const base = (firstUser?.text || '').replace(/\s+/g, ' ').trim();
  if (!base) return 'New conversation';
  return base.length > 48 ? `${base.slice(0, 47)}…` : base;
};

// "14 msgs · 19 Jun 17:50"
const formatSessionMeta = (s: StoredSession): string => {
  const d = new Date(s.updatedAt);
  const valid = !Number.isNaN(d.getTime());
  const stamp = valid
    ? d.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : '';
  const count = `${s.msgCount} msg${s.msgCount === 1 ? '' : 's'}`;
  return stamp ? `${count} · ${stamp}` : count;
};

// Render bot replies as markdown. Agents emit standard markdown (**bold**,
// ## Headers, [text](url), - lists, `code`, ```code blocks```), so we render
// it via react-markdown with GFM (tables, strikethrough, autolinks).
//
// Tailwind doesn't auto-style raw HTML descendants without @tailwindcss/typography,
// which we don't depend on, so each element is given inline styles tuned for
// the dark glass bubble.
// Markdown collapses SINGLE newlines into spaces, so a reply written with
// line breaks (lists of short lines, addresses, step lists) "flushes together"
// into one run-on paragraph. Preserve intra-paragraph line breaks as markdown
// hard breaks (two trailing spaces) while leaving blank-line paragraph breaks
// intact. No lookbehind, so it's safe on older Safari.
function withSoftBreaks(text: string): string {
  if (!text) return text;
  return text
    .split(/\n{2,}/)
    .map((para) => para.split('\n').join('  \n'))
    .join('\n\n');
}

const BotMarkdown: React.FC<{ text: string }> = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => <p className="m-0 mb-2 last:mb-0">{children}</p>,
      h1: ({ children }) => <p className="m-0 mb-2 text-base font-semibold">{children}</p>,
      h2: ({ children }) => <p className="m-0 mb-2 text-base font-semibold">{children}</p>,
      h3: ({ children }) => <p className="m-0 mb-2 text-sm font-semibold">{children}</p>,
      h4: ({ children }) => <p className="m-0 mb-2 text-sm font-semibold">{children}</p>,
      strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
      ul: ({ children }) => <ul className="m-0 mb-2 list-disc pl-5 last:mb-0">{children}</ul>,
      ol: ({ children }) => <ol className="m-0 mb-2 list-decimal pl-5 last:mb-0">{children}</ol>,
      li: ({ children }) => <li className="mb-0.5">{children}</li>,
      a: ({ children, href }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sky-300 underline decoration-dotted underline-offset-2 hover:text-sky-200"
        >
          {children}
        </a>
      ),
      code: ({ children, className }) => {
        const isBlock = (className || '').startsWith('language-');
        if (isBlock) {
          return (
            <pre className="m-0 mb-2 overflow-x-auto rounded-md bg-black/40 p-2 last:mb-0">
              <code className="text-xs text-zinc-100 font-mono leading-relaxed">{children}</code>
            </pre>
          );
        }
        return (
          <code className="rounded bg-white/10 px-1 py-0.5 text-[0.85em] font-mono">
            {children}
          </code>
        );
      },
      blockquote: ({ children }) => (
        <blockquote className="m-0 mb-2 border-l-2 border-white/30 pl-3 italic text-zinc-200/90 last:mb-0">
          {children}
        </blockquote>
      ),
      hr: () => <hr className="my-2 border-white/10" />,
      table: ({ children }) => (
        <div className="my-2 overflow-x-auto">
          <table className="w-full border-collapse text-xs">{children}</table>
        </div>
      ),
      th: ({ children }) => (
        <th className="border-b border-white/15 px-2 py-1 text-left font-semibold">{children}</th>
      ),
      td: ({ children }) => <td className="border-b border-white/5 px-2 py-1">{children}</td>,
    }}
  >
    {withSoftBreaks(text)}
  </ReactMarkdown>
);

// Liquid-glass SVG distortion filter — adds the subtle "wet glass" caustic
// effect under the panel's backdrop blur.
const GlassFilter: React.FC = () => (
  <svg style={{ position: 'absolute', width: 0, height: 0 }} aria-hidden>
    <filter
      id="qbz-glass-distortion"
      x="0%"
      y="0%"
      width="100%"
      height="100%"
      filterUnits="objectBoundingBox"
    >
      <feTurbulence
        type="fractalNoise"
        baseFrequency="0.005 0.012"
        numOctaves="1"
        seed="14"
        result="turbulence"
      />
      <feGaussianBlur in="turbulence" stdDeviation="2" result="softMap" />
      <feDisplacementMap
        in="SourceGraphic"
        in2="softMap"
        scale="40"
        xChannelSelector="R"
        yChannelSelector="G"
      />
    </filter>
  </svg>
);

const FloatingAiAssistant: React.FC<FloatingAiAssistantProps> = ({
  botId = 'bot_b859sa686oz0',
  wsUrl = DEFAULT_WS_URL,
}) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  // Sticky once true: the WS lives for the rest of the component's lifetime
  // so closing+reopening the launcher mid-reply doesn't drop the in-flight
  // bot response. The user can close, walk away, come back — the message
  // they sent is still being streamed in the background.
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [connected, setConnected] = useState(false);
  const [thinking, setThinking] = useState(false);
  // Compact agent-activity strip for the in-flight turn: which sub-agent is
  // running / which tool is being used. Small workflow hint, NOT the full
  // timeline or ui_schema cards the in-app chat renders. Cleared each turn.
  const [activity, setActivity] = useState<ActivityStep[]>([]);
  // Pending HITL question form (agent paused on ask_human) + the visitor's answers.
  const [hitlRequest, setHitlRequest] = useState<HitlRequest | null>(null);
  const [hitlAnswers, setHitlAnswers] = useState<Record<number, string | string[]>>({});
  const [hitlEditing, setHitlEditing] = useState(false);
  const [hitlEditText, setHitlEditText] = useState('');
  const [botName, setBotName] = useState<string>('Assistant');
  const [poweredByVisible, setPoweredByVisible] = useState<boolean>(true);
  // Past conversations (client-side history) + popover open state.
  const [sessions, setSessions] = useState<StoredSession[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  // The id of the conversation currently on screen. Mirrors sessionIdRef so
  // both the render path (this state) and the WS send path (the ref) agree.
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  // File attachments for the next message + the bot's upload capabilities.
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [uploadsEnabled, setUploadsEnabled] = useState(false);
  const uploadsCfgRef = useRef<UploadsConfig>({
    enabled: false,
    maxBytes: 25 * 1024 * 1024,
    maxFiles: 5,
    allowedExt: [],
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // upload_ref → resolver, so the async `presign` WS reply can hand the URL
  // back to the awaiting handleFiles() call.
  const pendingUploadsRef = useRef<Map<string, (r: { presigned_url: string; s3_key: string; bucket?: string } | null) => void>>(
    new Map(),
  );

  // Promote isChatOpen=true to hasOpenedOnce=true the first time the
  // launcher is clicked. Stays sticky.
  useEffect(() => {
    if (isChatOpen && !hasOpenedOnce) setHasOpenedOnce(true);
  }, [isChatOpen, hasOpenedOnce]);

  // Detected once at mount: are we embedded in an iframe? Drives the
  // postMessage protocol below — no-op when running standalone.
  const isEmbedded = typeof window !== 'undefined' && window.parent !== window;

  const chatRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const historyRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const installIdRef = useRef<string>('');
  const visitorIdRef = useRef<string>('');
  const sessionIdRef = useRef<string>('');
  const pendingBotMsgIdRef = useRef<string | null>(null);
  // Connections to channels.qubitz.ai close after a single agent invocation
  // (AgentCore runtime WS lifecycle + ALB idle). On any close while the chat
  // is still open we transparently rebuild the WS and replay anything that
  // was queued while we were offline. The user shouldn't see drops.
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isUnmountingRef = useRef(false);
  const queuedSendsRef = useRef<Array<{ text: string; attachments?: Array<Record<string, unknown>> }>>([]);
  const readyRef = useRef(false); // true once we've received `ready` on the current WS
  // Set by the WS effect; called from handleSend when no socket exists yet.
  const reconnectRef = useRef<() => void>(() => {});
  // Visitor id the in-memory `sessions` list was loaded for. Used as the
  // localStorage namespace so we don't persist one visitor's history under
  // another's key if the visitor id resolves late (after `ready`).
  const sessionsVisitorRef = useRef<string>('');
  // When set, the next `widget_reset`-driven `ready` frame should start a
  // clean session instead of restoring (used by New Chat).
  const resettingRef = useRef(false);

  const appendOnce = (msg: ChatMessage) =>
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  const updateMsg = (id: string, patch: Partial<ChatMessage>) =>
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));

  // Flush anything that accumulated while we were reconnecting. Only runs
  // after we've heard `ready` on the new socket so install_id/visitor_id
  // refs are populated.
  const flushQueue = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (!installIdRef.current || !visitorIdRef.current) return;
    while (queuedSendsRef.current.length > 0) {
      const item = queuedSendsRef.current.shift()!;
      try {
        ws.send(JSON.stringify({
          action: 'widget_message',
          install_id: installIdRef.current,
          visitor_id: visitorIdRef.current,
          text: item.text,
          ...(item.attachments && item.attachments.length ? { attachments: item.attachments } : {}),
        }));
      } catch (e) {
        // Re-queue and stop — the next reconnect cycle will retry.
        queuedSendsRef.current.unshift(item);
        break;
      }
    }
  };

  // ───── WebSocket lifecycle
  // Gated on `hasOpenedOnce` (not `isChatOpen`) so the WS keeps streaming
  // when the user closes the chat panel mid-reply. Closing the launcher
  // is purely cosmetic — the connection only tears down on full unmount
  // (host page navigation or iframe destroy).
  useEffect(() => {
    if (!hasOpenedOnce) return;
    isUnmountingRef.current = false;

    const connect = () => {
      if (isUnmountingRef.current) return;
      if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN ||
                            wsRef.current.readyState === WebSocket.CONNECTING)) return;

      if (typeof window !== 'undefined') {
        visitorIdRef.current =
          visitorIdRef.current || window.localStorage.getItem(VISITOR_KEY) || '';
      }
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      readyRef.current = false;

      ws.addEventListener('open', () => {
        if (wsRef.current !== ws) {
          try { ws.close(); } catch { /* ignore */ }
          return;
        }
        setConnected(true);
        reconnectAttemptsRef.current = 0;
        ws.send(JSON.stringify({
          action: 'widget_public_config',
          bot_id: botId,
          origin: typeof window !== 'undefined' ? window.location.origin : '',
        }));
        ws.send(JSON.stringify({
          action: 'widget_connect',
          bot_id: botId,
          origin: typeof window !== 'undefined' ? window.location.origin : '',
          visitor_id: visitorIdRef.current,
        }));
      });

      ws.addEventListener('message', (ev) => {
        let data: any;
        try { data = JSON.parse(ev.data); } catch { return; }
        const t = data.type;
        // eslint-disable-next-line no-console
        console.debug('[widget WS]', t, data);

        if (t === 'public_config') {
          const a = (data.data && data.data.appearance) || {};
          if (a.botName) setBotName(a.botName);
          if (typeof a.poweredByVisible === 'boolean') setPoweredByVisible(a.poweredByVisible);
          if (a.welcomeMessage) appendOnce({ id: 'welcome', role: 'bot', text: a.welcomeMessage });
          const up = data.data && data.data.uploads;
          if (up && typeof up === 'object') {
            uploadsCfgRef.current = {
              enabled: !!up.enabled,
              maxBytes: typeof up.max_bytes === 'number' ? up.max_bytes : 25 * 1024 * 1024,
              maxFiles: typeof up.max_files === 'number' ? up.max_files : 5,
              allowedExt: Array.isArray(up.allowed_ext) ? up.allowed_ext : [],
            };
            setUploadsEnabled(!!up.enabled);
          }
          return;
        }
        if (t === 'presign') {
          const d = data.data || {};
          const resolve = pendingUploadsRef.current.get(d.upload_ref);
          if (resolve) {
            pendingUploadsRef.current.delete(d.upload_ref);
            resolve(d.error ? null : { presigned_url: d.presigned_url, s3_key: d.s3_key, bucket: d.bucket });
          }
          return;
        }
        if (t === 'ready') {
          const d = data.data || data;
          installIdRef.current = d.install_id;
          visitorIdRef.current = d.visitor_id;
          if (typeof window !== 'undefined') window.localStorage.setItem(VISITOR_KEY, d.visitor_id);
          readyRef.current = true;

          // `ready` arrives on the very first connect, on every transparent
          // reconnect, and as the reply to a `widget_reset` (New Chat). Only
          // adopt the server's session_id when we're starting fresh — a New
          // Chat reset, or the first time we have no current session at all.
          // On a plain mid-conversation reconnect we keep the id we already
          // have so we don't fork the on-screen thread.
          const incomingSid: string = d.session_id || '';
          const startingFresh = resettingRef.current || !sessionIdRef.current;
          if (startingFresh && incomingSid) {
            sessionIdRef.current = incomingSid;
            setCurrentSessionId(incomingSid);
          }
          resettingRef.current = false;

          // First time we learn the visitor id, hydrate this browser's saved
          // conversations for that visitor.
          if (d.visitor_id && sessionsVisitorRef.current !== d.visitor_id) {
            sessionsVisitorRef.current = d.visitor_id;
            setSessions(loadSessions(botId, d.visitor_id));
          }

          flushQueue();
          return;
        }
        if (t === 'thinking') {
          setThinking(true);
          setActivity([]);
          const id = `bot_${Date.now()}`;
          pendingBotMsgIdRef.current = id;
          appendOnce({ id, role: 'bot', text: '', pending: true });
          return;
        }
        if (t === 'activity') {
          const d = data.data || {};
          const raw = String(d.label || '').slice(0, 60);
          if (!raw) return;
          const kind = d.kind === 'tool' ? 'tool' : 'agent';
          const state: ActivityStep['state'] = d.state === 'done' || d.state === 'error' ? d.state : 'active';
          const label = prettyActivity(kind, raw);
          const key = label.toLowerCase();
          setActivity((prev) => {
            // Systematic, ordered log: dedupe by the humanized label (so a
            // sub-agent and the coordinator's tool-call for the same name merge
            // into ONE step), only one step "active" at a time, and never
            // downgrade a finished step back to active on a late duplicate event.
            const next: ActivityStep[] = state === 'active' ? prev.map((s) => (s.state === 'active' ? { ...s, state: 'done' as const } : s)) : prev.slice();
            const idx = next.findIndex((s) => s.label.toLowerCase() === key);
            if (idx >= 0) {
              const keepDone = state === 'active' && next[idx].state !== 'active';
              next[idx] = { label, state: keepDone ? next[idx].state : state };
            } else {
              next.push({ label, state });
            }
            return next.slice(-8);
          });
          return;
        }
        if (t === 'hitl_request') {
          const d = data.data || {};
          if (!d.interrupt_id) return;
          setThinking(false); // the form is the active element, not the dots
          setHitlAnswers({});
          setHitlRequest({
            interrupt_id: d.interrupt_id,
            name: d.name,
            kind: d.kind,
            question: d.question || d.message || d.prompt || d.text,
            options: Array.isArray(d.options) ? d.options : undefined,
            questions: Array.isArray(d.questions) && d.questions.length ? d.questions : undefined,
            agent: d.agent,
            tool: d.tool,
            tool_input: d.tool_input,
            draft: d.draft,
          });
          // Reflect the pause in the activity log as a LIVE step (blue spinner),
          // not a done check — the workflow is paused, waiting on the visitor.
          // Stays active until the answer resumes the run (new events mark it done).
          {
            const isAsk =
              d.kind === 'ask' || d.name === 'ask_human' || (Array.isArray(d.questions) && d.questions.length > 0);
            const waitLabel = isAsk ? 'Waiting for your answer' : 'Waiting for your approval';
            setActivity((prev) => {
              const next: ActivityStep[] = prev.map((s) => (s.state === 'active' ? { ...s, state: 'done' as const } : s));
              const idx = next.findIndex((s) => s.label === waitLabel);
              if (idx >= 0) next[idx] = { label: waitLabel, state: 'active' };
              else next.push({ label: waitLabel, state: 'active' });
              return next.slice(-8);
            });
          }
          return;
        }
        if (t === 'delta') {
          const id = pendingBotMsgIdRef.current;
          if (!id) return;
          setMessages((prev) =>
            prev.map((m) => (m.id === id ? { ...m, text: m.text + (data.text || '') } : m)),
          );
          return;
        }
        if (t === 'complete') {
          setThinking(false);
          setActivity([]);
          setHitlRequest(null);
          const id = pendingBotMsgIdRef.current;
          const d = data.data || {};
          const finalText = d.text || d.result || d.response || '';
          if (id) {
            updateMsg(id, { text: finalText || '(no response)', pending: false });
            pendingBotMsgIdRef.current = null;
          } else if (finalText) {
            appendOnce({ id: `bot_${Date.now()}`, role: 'bot', text: finalText });
          }
          return;
        }
        if (t === 'error') {
          setThinking(false);
          setActivity([]);
          setHitlRequest(null);
          const id = pendingBotMsgIdRef.current;
          const errText = `Sorry, ${data.message || data.error || 'something went wrong'}.`;
          if (id) {
            updateMsg(id, { text: errText, pending: false });
            pendingBotMsgIdRef.current = null;
          } else {
            appendOnce({ id: `err_${Date.now()}`, role: 'system', text: errText });
          }
          return;
        }
      });

      ws.addEventListener('close', (ev) => {
        setConnected(false);
        if (wsRef.current === ws) wsRef.current = null;
        readyRef.current = false;
        // eslint-disable-next-line no-console
        console.debug('[widget WS] close', { code: ev.code, reason: ev.reason });
        if (isUnmountingRef.current) return;

        // Auto-reconnect with bounded exponential backoff. Max 6 attempts so a
        // truly broken backend doesn't loop forever.
        const n = reconnectAttemptsRef.current;
        if (n >= 6) {
          const pid = pendingBotMsgIdRef.current;
          if (pid) {
            setThinking(false);
            updateMsg(pid, {
              text: "Sorry — I can't reach the server right now. Please try again in a moment.",
              pending: false,
            });
            pendingBotMsgIdRef.current = null;
          }
          return;
        }
        reconnectAttemptsRef.current = n + 1;
        const delay = Math.min(2000, 200 * Math.pow(2, n)); // 200, 400, 800, 1600, 2000, 2000
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(connect, delay);
      });
      ws.addEventListener('error', () => setConnected(false));
    };

    reconnectRef.current = connect;
    connect();

    return () => {
      isUnmountingRef.current = true;
      reconnectRef.current = () => {};
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      try { wsRef.current?.close(); } catch { /* ignore */ }
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOpenedOnce, botId, wsUrl]);

  // Only auto-scroll when the user is already near the bottom — don't fight
  // them if they've scrolled up to read earlier messages.
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 80) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
    // Also re-scroll when the activity log grows or a HITL form appears — those
    // render below the messages but aren't part of `messages`, so without this
    // they can show up below the fold and the visitor never sees them.
  }, [messages, activity, hitlRequest]);

  // ───── Client-side history
  // Whether a message list is worth persisting: anything beyond the lone
  // welcome bubble (which every fresh session starts with).
  const isWorthSaving = (msgs: ChatMessage[]) =>
    msgs.some((m) => m.role === 'user' || (m.role === 'bot' && m.id !== 'welcome'));

  // Merge the current conversation into the saved list under its session id.
  const upsertSession = useCallback(
    (sid: string, msgs: ChatMessage[]) => {
      if (!sid || !isWorthSaving(msgs)) return;
      const visitor = sessionsVisitorRef.current || visitorIdRef.current;
      // Drop the live typing-dots placeholder before persisting.
      const clean = msgs.filter((m) => !(m.pending && !m.text));
      const next: StoredSession = {
        id: sid,
        title: deriveTitle(clean),
        messages: clean,
        msgCount: clean.length,
        updatedAt: Date.now(),
      };
      setSessions((prev) => {
        const others = prev.filter((s) => s.id !== sid);
        const merged = [next, ...others].sort((a, b) => b.updatedAt - a.updatedAt);
        saveSessions(botId, visitor, merged);
        return merged;
      });
    },
    [botId],
  );

  // Autosave the CURRENT session whenever its messages change. Skips the
  // typing-dots-only state so we don't thrash storage mid-stream.
  useEffect(() => {
    if (!currentSessionId) return;
    if (thinking) return; // wait until the reply lands to avoid partial saves
    upsertSession(currentSessionId, messages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, currentSessionId, thinking]);

  // New Chat — snapshot the current conversation into history, clear the
  // view, and ask the backend for a fresh server-side session id (preserving
  // AgentCore memory isolation). The reset's `ready` reply adopts the new id.
  const startNewChat = useCallback(() => {
    if (currentSessionId) upsertSession(currentSessionId, messages);
    setHistoryOpen(false);
    setMessage('');
    setAttachments([]);
    pendingBotMsgIdRef.current = null;
    setThinking(false);
    setMessages([]);
    // Force the next `ready` to adopt the server's fresh session_id.
    resettingRef.current = true;
    sessionIdRef.current = '';
    setCurrentSessionId('');
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && installIdRef.current && visitorIdRef.current) {
      try {
        ws.send(JSON.stringify({
          action: 'widget_reset',
          install_id: installIdRef.current,
          visitor_id: visitorIdRef.current,
        }));
      } catch {
        // No live socket → kick a reconnect; first `ready` adopts a new id.
        reconnectAttemptsRef.current = 0;
        reconnectRef.current();
      }
    } else {
      reconnectAttemptsRef.current = 0;
      reconnectRef.current();
    }
  }, [currentSessionId, messages, upsertSession]);

  // Resume a past conversation: snapshot whatever is on screen, then restore
  // the chosen session's messages and re-adopt its id for the live thread.
  const resumeSession = useCallback(
    (sid: string) => {
      if (sid === currentSessionId) {
        setHistoryOpen(false);
        return;
      }
      if (currentSessionId) upsertSession(currentSessionId, messages);
      const target = sessions.find((s) => s.id === sid);
      if (!target) {
        setHistoryOpen(false);
        return;
      }
      pendingBotMsgIdRef.current = null;
      setThinking(false);
      setMessage('');
      setAttachments([]);
      setMessages(target.messages);
      sessionIdRef.current = sid;
      setCurrentSessionId(sid);
      // We're resuming an existing thread, not minting a new one.
      resettingRef.current = false;
      // Tell the backend to point this visitor's CURRENT session at the resumed
      // id, so the agent's AgentCore memory for that conversation continues —
      // not just the messages we restored on screen. Mirrors widget_reset; the
      // `ready` reply confirms the adopted id (already === sid locally). If the
      // socket isn't open, the visible thread is still restored; the server-side
      // switch lands on the next widget_resume once connected.
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN && installIdRef.current && visitorIdRef.current) {
        try {
          ws.send(JSON.stringify({
            action: 'widget_resume',
            install_id: installIdRef.current,
            visitor_id: visitorIdRef.current,
            session_id: sid,
          }));
        } catch {
          /* non-fatal — a later message still uses the scope's current session */
        }
      }
      setHistoryOpen(false);
    },
    [currentSessionId, messages, sessions, upsertSession],
  );

  const deleteSession = useCallback(
    (sid: string) => {
      const visitor = sessionsVisitorRef.current || visitorIdRef.current;
      setSessions((prev) => {
        const next = prev.filter((s) => s.id !== sid);
        saveSessions(botId, visitor, next);
        return next;
      });
    },
    [botId],
  );

  // Ask the Channel runtime for a presigned PUT URL over the live WS. Resolves
  // when the matching `presign` reply arrives (or null on failure/timeout).
  const requestPresign = (ref: string, file: File): Promise<{ presigned_url: string; s3_key: string; bucket?: string } | null> =>
    new Promise((resolve) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN || !readyRef.current || !installIdRef.current) {
        resolve(null);
        return;
      }
      pendingUploadsRef.current.set(ref, resolve);
      try {
        ws.send(JSON.stringify({
          action: 'widget_presign',
          install_id: installIdRef.current,
          visitor_id: visitorIdRef.current,
          upload_ref: ref,
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          file_size: file.size,
        }));
      } catch {
        pendingUploadsRef.current.delete(ref);
        resolve(null);
        return;
      }
      setTimeout(() => {
        if (pendingUploadsRef.current.has(ref)) {
          pendingUploadsRef.current.delete(ref);
          resolve(null);
        }
      }, 15000);
    });

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const cfg = uploadsCfgRef.current;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (attachments.length + i >= cfg.maxFiles) {
        appendOnce({ id: `sys_${Date.now()}_${i}`, role: 'system', text: `You can attach up to ${cfg.maxFiles} files.` });
        break;
      }
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (cfg.allowedExt.length && !cfg.allowedExt.includes(ext)) {
        appendOnce({ id: `sys_${Date.now()}_${i}`, role: 'system', text: `.${ext} files aren't supported.` });
        continue;
      }
      if (file.size > cfg.maxBytes) {
        appendOnce({ id: `sys_${Date.now()}_${i}`, role: 'system', text: `${file.name} is too large (max ${Math.round(cfg.maxBytes / 1024 / 1024)} MB).` });
        continue;
      }
      const id = `att_${Date.now()}_${i}`;
      const ref = `up_${id}`;
      setAttachments((p) => [...p, { id, name: file.name, size: file.size, mime: file.type || 'application/octet-stream', status: 'uploading' }]);
      const presign = await requestPresign(ref, file);
      if (!presign) {
        setAttachments((p) => p.map((a) => (a.id === id ? { ...a, status: 'error' } : a)));
        continue;
      }
      try {
        const put = await fetch(presign.presigned_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!put.ok) throw new Error(String(put.status));
        setAttachments((p) => p.map((a) => (a.id === id ? { ...a, status: 'ready', s3Key: presign.s3_key, bucket: presign.bucket } : a)));
      } catch {
        setAttachments((p) => p.map((a) => (a.id === id ? { ...a, status: 'error' } : a)));
      }
    }
  };

  const removeAttachment = (id: string) => setAttachments((p) => p.filter((a) => a.id !== id));

  // Submit the visitor's answer to a paused HITL question. Sends a
  // widget_hitl_response frame (mirrors the in-app ApprovalCard decision shape:
  // { answer: formatted, answers: [{question, answer}] }); the agent resumes the
  // SAME run and streams its continuation into the existing pending bubble.
  // Send ANY HITL decision (ask answer, or approve/edit/reject) and resume the
  // run. `echo` is a short line dropped into the transcript so the visitor sees
  // what they chose; the agent's resumed output streams into the pending bubble.
  const sendHitlDecision = (decision: Record<string, unknown>) => {
    const req = hitlRequest;
    if (!req) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(
          JSON.stringify({
            action: 'widget_hitl_response',
            install_id: installIdRef.current,
            visitor_id: visitorIdRef.current,
            interrupt_id: req.interrupt_id,
            decision,
          }),
        );
      } catch {
        /* ignore — the agent's own HITL timeout will resume it */
      }
    }
    // Do NOT echo the answer/decision into the transcript — it's a control input,
    // not a chat message, and echoing it dumped the visitor's HITL answers into
    // the conversation (appearing out of order after the agent's reply). The
    // form just clears and the agent's resumed output streams into the bubble.
    setHitlRequest(null);
    setHitlAnswers({});
    setHitlEditing(false);
    setHitlEditText('');
    setThinking(true);
  };

  // ask_human → free-form / MCQ answer (decision = { answer, answers }).
  const submitHitl = () => {
    const req = hitlRequest;
    if (!req) return;
    const qs = hitlQuestions(req);
    const formatted = qs
      .map((q, i) => {
        const a = hitlAnswers[i];
        const ans = Array.isArray(a) ? a.join(', ') : (a || '');
        return qs.length > 1 ? `${q.question} → ${ans}` : ans;
      })
      .join('\n');
    sendHitlDecision({
      answer: formatted,
      answers: qs.map((q, i) => ({ question: q.question, answer: hitlAnswers[i] ?? '' })),
    });
  };

  const hitlReady = (() => {
    if (!hitlRequest) return false;
    return hitlQuestions(hitlRequest).every((_, i) => {
      const a = hitlAnswers[i];
      return Array.isArray(a) ? a.length > 0 : typeof a === 'string' && a.trim().length > 0;
    });
  })();

  const handleSend = () => {
    const text = message.trim();
    if (!text) return;
    // Don't send mid-upload — the s3_key isn't ready yet.
    if (attachments.some((a) => a.status === 'uploading')) return;

    const ready = attachments.filter((a) => a.status === 'ready' && a.s3Key);
    const attPayload = ready.map((a) => ({
      s3_key: a.s3Key as string,
      name: a.name,
      mime_type: a.mime,
      size: a.size,
      ...(a.bucket ? { bucket: a.bucket } : {}),
    }));

    appendOnce({
      id: `user_${Date.now()}`,
      role: 'user',
      text,
      attachments: ready.length ? ready.map((a) => ({ name: a.name })) : undefined,
    });
    setMessage('');
    setAttachments([]);

    const ws = wsRef.current;
    const isReady =
      ws &&
      ws.readyState === WebSocket.OPEN &&
      readyRef.current &&
      installIdRef.current &&
      visitorIdRef.current;

    if (isReady) {
      try {
        ws!.send(JSON.stringify({
          action: 'widget_message',
          install_id: installIdRef.current,
          visitor_id: visitorIdRef.current,
          text,
          ...(attPayload.length ? { attachments: attPayload } : {}),
        }));
        return;
      } catch {
        // fall through to queue + reconnect
      }
    }

    // Not ready: still handshaking, just dropped, or send threw. Queue the
    // message (with its attachments) — the `ready` handler on the new socket
    // will flush it. If there's no live socket at all, kick a fresh connect.
    queuedSendsRef.current.push({ text, attachments: attPayload.length ? attPayload : undefined });
    if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
      reconnectAttemptsRef.current = 0;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectRef.current();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Click outside to close (only meaningful in standalone mode — embedded
  // mode gets its outside clicks via the loader resizing the iframe so the
  // host page reclaims the area).
  useEffect(() => {
    if (isEmbedded) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (chatRef.current && !chatRef.current.contains(target)) {
        if (!target.closest('.qbz-launcher')) setIsChatOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isEmbedded]);

  // Dismiss the history popover on any click outside it (works in both
  // embedded and standalone modes since it's scoped within the panel).
  useEffect(() => {
    if (!historyOpen) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (historyRef.current && !historyRef.current.contains(target)) {
        setHistoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [historyOpen]);

  // ───── iframe protocol — only active when embedded
  // Tell the loader to grow/shrink the iframe so the host page is unblocked
  // when the chat is closed and gets enough space when it's open.
  useEffect(() => {
    if (!isEmbedded) return;
    const SIZE_CLOSED = { width: 84, height: 84 };
    const SIZE_OPEN = { width: 440, height: 720 };
    const target = isChatOpen ? SIZE_OPEN : SIZE_CLOSED;
    window.parent.postMessage(
      { type: 'qubitz-widget-resize', width: target.width, height: target.height },
      '*',
    );
  }, [isChatOpen, isEmbedded]);

  // Accept open/close/toggle commands from the loader's window.Qubitz API.
  useEffect(() => {
    if (!isEmbedded) return;
    const handler = (e: MessageEvent) => {
      const msg = e.data;
      if (!msg || typeof msg !== 'object') return;
      if (msg.type === 'qubitz-widget-open') setIsChatOpen(true);
      else if (msg.type === 'qubitz-widget-close') setIsChatOpen(false);
      else if (msg.type === 'qubitz-widget-toggle') setIsChatOpen((v) => !v);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isEmbedded]);

  return (
    <div className="fixed bottom-6 right-6 z-50 font-sans">
      <GlassFilter />

      {/* ───── Dark glass launcher — matches the chat panel's tint, with a
              little backdrop showing through. Not flat black, not bright. ───── */}
      <button
        className={`qbz-launcher relative h-14 w-14 rounded-full flex items-center justify-center transition-all duration-500 ${
          isChatOpen ? 'rotate-90 scale-95' : 'rotate-0 scale-100'
        } hover:scale-105 active:scale-95`}
        onClick={() => setIsChatOpen(!isChatOpen)}
        style={{
          background:
            'linear-gradient(135deg, rgba(24,24,27,0.65) 0%, rgba(9,9,11,0.72) 100%)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          boxShadow: '0 8px 24px -6px rgba(0,0,0,0.45)',
          border: '1px solid rgba(255, 255, 255, 0.10)',
        }}
        aria-label={isChatOpen ? 'Close chat' : 'Open chat'}
      >
        <span className="relative z-10 text-white drop-shadow">
          {isChatOpen ? <X className="h-5 w-5" /> : <Bot className="h-6 w-6" />}
        </span>
      </button>

      {/* ───── Liquid-glass chat panel ───── */}
      {isChatOpen && (
        <div
          ref={chatRef}
          className="absolute bottom-20 right-0 w-[400px] origin-bottom-right"
          style={{ animation: 'qbz-popIn 0.32s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards' }}
        >
          <div
            className="relative flex flex-col overflow-hidden rounded-3xl"
            style={{
              // Heavy blur with darker, more opaque tint so a busy retail
              // background never bleeds through behind chat text.
              background:
                'linear-gradient(135deg, rgba(20,20,23,0.88) 0%, rgba(9,9,11,0.92) 100%)',
              backdropFilter: 'blur(80px) saturate(180%)',
              WebkitBackdropFilter: 'blur(80px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.10)',
              boxShadow:
                '0 25px 60px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
            }}
          >
            {/* Specular sheen across the top */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-px"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
              }}
            />

            {/* Header */}
            <div className="relative flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div
                  className={`relative h-2.5 w-2.5 rounded-full ${connected ? 'bg-emerald-400' : 'bg-zinc-500'}`}
                >
                  {connected && (
                    <span className="absolute inset-0 rounded-full bg-emerald-400/70 animate-ping" />
                  )}
                </div>
                <span className="text-sm font-medium text-zinc-100/95">{botName}</span>
              </div>

              <div className="flex items-center gap-1">
                {/* New Chat */}
                <button
                  onClick={startNewChat}
                  className="rounded-full p-1.5 text-zinc-300/80 hover:bg-white/5 hover:text-zinc-100 transition-colors"
                  aria-label="New chat"
                  title="New chat"
                >
                  <MessageSquarePlus className="h-4 w-4" />
                </button>

                {/* History + popover */}
                <div ref={historyRef} className="relative">
                  <button
                    onClick={() => setHistoryOpen((v) => !v)}
                    className={`flex items-center gap-1 rounded-full pl-2 pr-2 py-1 text-[11px] font-medium transition-colors ${
                      historyOpen
                        ? 'bg-white/[0.10] text-zinc-100'
                        : 'text-zinc-300/80 hover:bg-white/5 hover:text-zinc-100'
                    }`}
                    style={{ border: '1px solid rgba(255,255,255,0.10)' }}
                    aria-label="Past sessions"
                    aria-expanded={historyOpen}
                    title="Past sessions"
                  >
                    <History className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">History</span>
                    <span className="rounded-full bg-white/10 px-1.5 leading-tight text-zinc-200">
                      {sessions.length}
                    </span>
                  </button>

                  {historyOpen && (
                    <div
                      className="absolute right-0 top-full z-20 mt-2 w-72 origin-top-right overflow-hidden rounded-2xl"
                      style={{
                        background:
                          'linear-gradient(135deg, rgba(24,24,27,0.96) 0%, rgba(9,9,11,0.97) 100%)',
                        backdropFilter: 'blur(40px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                        border: '1px solid rgba(255,255,255,0.10)',
                        boxShadow:
                          '0 20px 50px -12px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)',
                        animation: 'qbz-popIn 0.2s cubic-bezier(0.175,0.885,0.32,1.275) forwards',
                      }}
                    >
                      <div className="px-3.5 pt-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                        Past sessions
                      </div>
                      <div className="max-h-72 overflow-y-auto pb-1.5">
                        {sessions.length === 0 ? (
                          <div className="px-3.5 py-5 text-center text-xs text-zinc-500">
                            No past conversations yet.
                          </div>
                        ) : (
                          sessions.map((s) => (
                            <div
                              key={s.id}
                              className={`group relative flex items-start gap-2.5 px-3.5 py-2.5 transition-colors hover:bg-white/[0.06] ${
                                s.id === currentSessionId ? 'bg-white/[0.05]' : ''
                              }`}
                            >
                              <button
                                onClick={() => resumeSession(s.id)}
                                className="flex min-w-0 flex-1 items-start gap-2.5 text-left"
                                title="Resume this conversation"
                              >
                                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-zinc-300">
                                  <MessageSquare className="h-3.5 w-3.5" />
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-[13px] font-medium text-zinc-100">
                                    {s.title}
                                  </span>
                                  <span className="mt-0.5 block truncate text-[11px] text-zinc-500">
                                    {formatSessionMeta(s)}
                                  </span>
                                </span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteSession(s.id);
                                }}
                                className="mt-0.5 shrink-0 rounded-md p-1 text-zinc-600 opacity-0 transition hover:bg-white/10 hover:text-zinc-200 group-hover:opacity-100"
                                aria-label={`Delete ${s.title}`}
                                title="Delete"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setIsChatOpen(false)}
                  className="rounded-full p-1.5 text-zinc-300/80 hover:bg-white/5 hover:text-zinc-100 transition-colors"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={messagesContainerRef}
              className="relative flex-1 overflow-y-auto px-4 py-4 max-h-[420px] min-h-[280px] space-y-2.5"
            >
              {messages.length === 0 && !thinking && (
                <div className="text-center text-xs text-zinc-500 py-6">
                  {connected ? 'Say hello…' : 'Connecting…'}
                </div>
              )}
              {messages
                .filter((m) => !(m.pending && !m.text && hitlRequest))
                .map((m) => (
                <div
                  key={m.id}
                  className={`flex ${
                    m.role === 'user' ? 'justify-end' : m.role === 'system' ? 'justify-center' : 'justify-start'
                  }`}
                >
                  {m.role === 'system' ? (
                    <div className="rounded-md bg-amber-500/10 px-3 py-1.5 text-xs text-amber-300 border border-amber-500/20">
                      {m.text}
                    </div>
                  ) : (
                    <div
                      className={`relative max-w-[85%] break-words rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                        m.role === 'user'
                          ? 'rounded-br-md text-zinc-900 whitespace-pre-wrap'
                          : 'rounded-bl-md text-zinc-100'
                      }`}
                      style={
                        m.role === 'user'
                          ? {
                              background:
                                'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(229,231,235,0.92))',
                              boxShadow:
                                '0 4px 12px -2px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.7)',
                            }
                          : {
                              background:
                                'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                              border: '1px solid rgba(255,255,255,0.08)',
                              backdropFilter: 'blur(8px)',
                              WebkitBackdropFilter: 'blur(8px)',
                              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
                            }
                      }
                    >
                      {m.pending && !m.text ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400" style={{ animationDelay: '0ms' }} />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400" style={{ animationDelay: '150ms' }} />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400" style={{ animationDelay: '300ms' }} />
                        </span>
                      ) : m.role === 'bot' ? (
                        <BotMarkdown text={m.text} />
                      ) : (
                        <>
                          {m.attachments && m.attachments.length > 0 && (
                            <div className="mb-1.5 flex flex-wrap gap-1">
                              {m.attachments.map((a, i) => (
                                <span
                                  key={i}
                                  className="inline-flex max-w-[160px] items-center gap-1 rounded-md bg-black/10 px-1.5 py-0.5 text-[11px] text-zinc-700"
                                >
                                  <Paperclip className="h-3 w-3 shrink-0" />
                                  <span className="truncate">{a.name}</span>
                                </span>
                              ))}
                            </div>
                          )}
                          {m.text}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {/* Agent-activity log — a small, systematic step list of what the
                  agent is doing this turn (humanized, deduped, ordered). Not the
                  full workflow timeline / ui_schema cards the in-app chat renders. */}
              {activity.length > 0 && (
                <div
                  className="mb-1 ml-1 max-w-[85%] space-y-1 rounded-xl px-3 py-2"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                >
                  {activity.map((s, i) => (
                    <div key={`${s.label}-${i}`} className="flex items-center gap-2 text-[11px] leading-tight text-zinc-300/90">
                      {/* Mirror basetemplate workflow-timeline: running = blue
                          spinning CircleDotDashed, done = emerald check, error = red X. */}
                      {s.state === 'active' ? (
                        <CircleDotDashed className="h-3.5 w-3.5 shrink-0 animate-spin text-blue-400" />
                      ) : s.state === 'error' ? (
                        <CircleX className="h-3.5 w-3.5 shrink-0 text-red-400" />
                      ) : (
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
                      )}
                      <span className="truncate">{s.label}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* HITL question form — the agent paused (ask_human) and wants the
                  visitor's input. Styled to match the bot bubble; on submit the
                  same run resumes. Single or multi-question, choice or free text. */}
              {hitlRequest && (
                <div
                  className="mb-1 ml-1 max-w-[90%] space-y-3 rounded-2xl rounded-bl-md px-3.5 py-3"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                    border: '1px solid rgba(255,255,255,0.10)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                  }}
                >
                  {isAskHitl(hitlRequest) ? (
                    <>
                      {hitlQuestions(hitlRequest).map((q, i) => {
                        const multi = !!q.allow_multi;
                        const opts = q.options || [];
                        const cur = hitlAnswers[i];
                        return (
                          <div key={i} className="space-y-1.5">
                            <p className="text-[13px] font-medium leading-snug text-zinc-100">{q.question}</p>
                            {opts.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {opts.map((opt) => {
                                  const selected = multi ? Array.isArray(cur) && cur.includes(opt) : cur === opt;
                                  return (
                                    <button
                                      key={opt}
                                      type="button"
                                      onClick={() =>
                                        setHitlAnswers((p) => {
                                          if (multi) {
                                            const arr = Array.isArray(p[i]) ? [...(p[i] as string[])] : [];
                                            const at = arr.indexOf(opt);
                                            if (at >= 0) arr.splice(at, 1);
                                            else arr.push(opt);
                                            return { ...p, [i]: arr };
                                          }
                                          return { ...p, [i]: opt };
                                        })
                                      }
                                      className={`rounded-full px-3 py-1 text-[12px] transition-colors ${
                                        selected ? 'text-emerald-100' : 'text-zinc-200 hover:bg-white/10'
                                      }`}
                                      style={{
                                        background: selected ? 'rgba(16,185,129,0.18)' : 'rgba(255,255,255,0.04)',
                                        border: selected ? '1px solid rgba(16,185,129,0.5)' : '1px solid rgba(255,255,255,0.12)',
                                      }}
                                    >
                                      {opt}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <input
                                type="text"
                                placeholder="Type your answer…"
                                value={typeof cur === 'string' ? cur : ''}
                                onChange={(e) => setHitlAnswers((p) => ({ ...p, [i]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && hitlReady) submitHitl();
                                }}
                                className="w-full rounded-xl px-3 py-2 text-[13px] text-zinc-100 placeholder:text-zinc-500 outline-none"
                                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
                              />
                            )}
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        disabled={!hitlReady}
                        onClick={submitHitl}
                        className="w-full rounded-xl px-3 py-2 text-[13px] font-medium text-zinc-900 transition disabled:opacity-40"
                        style={{
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(229,231,235,0.92))',
                          boxShadow: '0 4px 12px -2px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.7)',
                        }}
                      >
                        {hitlQuestions(hitlRequest).length > 1 ? 'Submit answers' : 'Send answer'}
                      </button>
                    </>
                  ) : (
                    // Approval gate (delegate / tool / output review) → approve / edit / reject
                    <>
                      <p className="text-[13px] font-medium leading-snug text-zinc-100">{hitlHeadline(hitlRequest)}</p>
                      {(() => {
                        const body =
                          hitlRequest.kind === 'output_review'
                            ? hitlRequest.draft || (typeof hitlRequest.tool_input === 'string' ? hitlRequest.tool_input : '')
                            : '';
                        return body ? (
                          <div
                            className="max-h-48 overflow-auto rounded-xl px-3 py-2 text-[12px] leading-relaxed text-zinc-200"
                            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}
                          >
                            <BotMarkdown text={String(body)} />
                          </div>
                        ) : null;
                      })()}
                      {hitlEditing ? (
                        <div className="space-y-2">
                          <textarea
                            value={hitlEditText}
                            onChange={(e) => setHitlEditText(e.target.value)}
                            placeholder="Edit…"
                            className="min-h-[120px] w-full rounded-xl px-3 py-2 text-[13px] text-zinc-100 placeholder:text-zinc-500 outline-none"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={!hitlEditText.trim()}
                              onClick={() => {
                                const r = hitlRequest;
                                const dec =
                                  r.kind === 'output_review'
                                    ? { action: 'edit', text: hitlEditText }
                                    : { action: 'edit', fields: { query: hitlEditText }, query: hitlEditText };
                                sendHitlDecision(dec);
                              }}
                              className="rounded-xl px-3 py-1.5 text-[12px] font-medium text-zinc-900 disabled:opacity-40"
                              style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(229,231,235,0.92))' }}
                            >
                              Save &amp; approve
                            </button>
                            <button
                              type="button"
                              onClick={() => { setHitlEditing(false); setHitlEditText(''); }}
                              className="rounded-xl px-3 py-1.5 text-[12px] text-zinc-300 hover:bg-white/10"
                              style={{ border: '1px solid rgba(255,255,255,0.12)' }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => sendHitlDecision({ action: 'approve' })}
                            className="rounded-xl px-3.5 py-1.5 text-[12px] font-medium text-emerald-100"
                            style={{ background: 'rgba(16,185,129,0.18)', border: '1px solid rgba(16,185,129,0.5)' }}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const r = hitlRequest;
                              const ti = r.tool_input;
                              const seed =
                                r.kind === 'output_review'
                                  ? r.draft || ''
                                  : ti && typeof ti === 'object' && 'query' in (ti as Record<string, unknown>)
                                    ? String((ti as Record<string, unknown>).query ?? '')
                                    : '';
                              setHitlEditText(seed);
                              setHitlEditing(true);
                            }}
                            className="rounded-xl px-3.5 py-1.5 text-[12px] text-zinc-200 hover:bg-white/10"
                            style={{ border: '1px solid rgba(255,255,255,0.12)' }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => sendHitlDecision({ action: 'reject' })}
                            className="rounded-xl px-3.5 py-1.5 text-[12px] text-red-300 hover:bg-red-500/10"
                            style={{ border: '1px solid rgba(255,255,255,0.12)' }}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div className="relative border-t border-white/10 px-3 py-2">
              {/* Pending attachment tray */}
              {attachments.length > 0 && (
                <div className="mb-1.5 flex flex-wrap gap-1.5">
                  {attachments.map((a) => (
                    <span
                      key={a.id}
                      className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] ${
                        a.status === 'error' ? 'bg-red-500/15 text-red-300' : 'bg-white/[0.08] text-zinc-200'
                      }`}
                      style={{ border: '1px solid rgba(255,255,255,0.10)' }}
                    >
                      {a.status === 'uploading' ? (
                        <span className="h-2.5 w-2.5 animate-spin rounded-full border border-zinc-400 border-t-transparent" />
                      ) : (
                        <Paperclip className="h-3 w-3 shrink-0" />
                      )}
                      <span className="max-w-[120px] truncate">{a.name}</span>
                      <button
                        onClick={() => removeAttachment(a.id)}
                        className="ml-0.5 text-zinc-400 hover:text-zinc-100"
                        aria-label={`Remove ${a.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div
                className="flex items-center gap-1.5 rounded-2xl pl-2 pr-1 py-0.5 transition focus-within:ring-2 focus-within:ring-white/15"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
                }}
              >
                {uploadsEnabled && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      accept={uploadsCfgRef.current.allowedExt.map((e) => '.' + e).join(',')}
                      onChange={(e) => {
                        void handleFiles(e.target.files);
                        e.currentTarget.value = '';
                      }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!connected || thinking || attachments.length >= uploadsCfgRef.current.maxFiles}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-zinc-300 transition hover:bg-white/5 hover:text-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Attach file"
                      title="Attach file"
                    >
                      <Paperclip className="h-4 w-4" />
                    </button>
                  </>
                )}
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  className="flex-1 resize-none bg-transparent text-sm leading-snug text-zinc-100 placeholder-zinc-500 focus:outline-none max-h-[110px] py-1.5"
                  placeholder={connected ? 'Ask a question...' : 'Connecting…'}
                  disabled={!connected || thinking}
                  style={{ scrollbarWidth: 'none' }}
                />

                <button
                  onClick={handleSend}
                  disabled={!connected || !message.trim() || thinking || attachments.some((a) => a.status === 'uploading')}
                  className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white transition-all duration-200 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 100%)',
                    backdropFilter: 'blur(16px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                    boxShadow: '0 2px 8px -2px rgba(0,0,0,0.35)',
                    border: '1px solid rgba(255, 255, 255, 0.20)',
                  }}
                  aria-label="Send"
                >
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>
              {poweredByVisible && (
                <div className="mt-2 text-center text-[11px] text-zinc-500">
                  Powered by{' '}
                  <a
                    href="https://qubitz.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-400 underline hover:text-zinc-200"
                  >
                    Qubitz
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes qbz-popIn {
          0% { opacity: 0; transform: scale(0.85) translateY(20px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export { FloatingAiAssistant };
